import {
  Badge,
  Box,
  HStack,
  Text,
  useMultiStyleConfig,
  VStack,
} from "@chakra-ui/react";
import { GenericTableBadgeColorEnum } from "gen/model/genericTableBadgeColorEnum";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Metadata,
  MetadataItem,
  MetadataItemProps,
} from "shared/components/metadata/Metadata";
import useDocumentTitle from "shared/hooks/useDocumentTitle";
import { ChevronLeftIcon } from "shared/icons/ChevronLeftIcon";

type PageHeaderMetadataItem = MetadataItemProps & {
  children?: MetadataItemProps[];
};

export interface PageHeaderBadge {
  text: string;
  color: GenericTableBadgeColorEnum;
}

export interface PageHeaderProps {
  size?: "sm" | "lg";
  title: string;
  documentTitle?: string;
  helperText?: string;
  workflowName?: string;
  backButton?: {
    title: string;
    path?: string;
  };
  badge?: PageHeaderBadge | React.ReactNode;
  actions?: React.ReactNode;
  stats?: React.ReactNode;
  metadata?: PageHeaderMetadataItem[];
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  documentTitle,
  workflowName,
  backButton,
  badge,
  actions,
  stats,
  metadata,
  size = workflowName ? "lg" : undefined,
  helperText,
}) => {
  useDocumentTitle(documentTitle ?? title);
  const styles = useMultiStyleConfig("PageHeader", { size });
  const navigate = useNavigate();
  return (
    <Box className="mb-page-header__row-container" sx={styles.rowContainer}>
      <Box className="mb-page-header__col-container" sx={styles.colContainer}>
        {backButton && (
          <HStack
            className="mb-page-header__backbutton"
            sx={styles.backButton}
            as={Link}
            {...(backButton.path
              ? {
                  to: backButton.path,
                }
              : {
                  onClick: (e) => {
                    e.preventDefault();
                    navigate(-1);
                  },
                })}
          >
            <ChevronLeftIcon />
            <Text>{backButton.title}</Text>
          </HStack>
        )}
        <HStack
          className="mb-page-header__title-and-actions"
          sx={styles.titleAndActions}
        >
          <HStack
            className="mb-page-header__title-and-stats"
            position="relative"
            flex="1"
            alignItems="flex-end"
          >
            <VStack
              className="mb-page-header__title-container"
              sx={styles.titleContainer}
              flex="1"
            >
              {workflowName && (
                <Text sx={styles.workflowName}>{workflowName}</Text>
              )}
              <Box className="mb-page-header__title" sx={styles.title}>
                {title}
                {badge &&
                  (isReactNode(badge) ? (
                    <Box
                      className="mb-page-header__title-badge"
                      sx={styles.titleBadge}
                    >
                      {badge}
                    </Box>
                  ) : (
                    <Badge
                      className="mb-page-header__title-badge"
                      sx={styles.titleBadge}
                      colorScheme={badge.color}
                    >
                      {badge.text}
                    </Badge>
                  ))}
              </Box>
            </VStack>
          </HStack>
          {actions && !stats && (
            <Box className="mb-page-header__actions" sx={styles.actions}>
              {actions}
            </Box>
          )}
        </HStack>
        {helperText && (
          <Text fontSize="12px" textColor="gray.600" mb="-8px" mt="8px">
            {helperText}
          </Text>
        )}
        {metadata && (
          <Metadata className="mb-page-header__metadata">
            {metadata.map(({ children, ...props }, index) => (
              <MetadataItem key={index} {...props}>
                {children?.map((child, childIndex) => (
                  <MetadataItem key={childIndex} {...child} />
                ))}
              </MetadataItem>
            ))}
          </Metadata>
        )}
      </Box>
      {stats && (
        <Box
          className="mb-page-header__stats-and-actions"
          sx={styles.statsAndActions}
        >
          {stats && <Box className="mb-page-header__stats">{stats}</Box>}
          {actions && stats && (
            <Box className="mb-page-header__actions" sx={styles.actions}>
              {actions}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const isReactNode = (
  badge: PageHeaderBadge | React.ReactNode,
): badge is React.ReactNode => React.isValidElement(badge);
